import React, { useState, useEffect } from 'react';
import config, { authFetch } from '../config';
import './ReviewsList.css';

const ReviewsList = ({ user, onSelectReview, onCreateReview, onLogout }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [activeGroup, setActiveGroup] = useState('all'); // 'all' | 'sharedWithMe'
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [shareLinkInput, setShareLinkInput] = useState('');
  const [loadingShareLink, setLoadingShareLink] = useState(false);

  const handleDeleteReview = async (e, reviewId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this review? This cannot be undone.')) return;

    try {
      const response = await authFetch(`${config.API_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete review');
      }
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
    } catch (err) {
      alert(err.message || 'Failed to delete review');
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const [ownRes, sharedRes] = await Promise.all([
        authFetch(`${config.API_URL}/api/reviews`),
        authFetch(`${config.API_URL}/api/shared/reviews`),
      ]);

      if (!ownRes.ok) throw new Error('Failed to load reviews');
      const ownData = await ownRes.json();
      setReviews(ownData.reviews || []);

      if (sharedRes.ok) {
        const sharedData = await sharedRes.json();
        setSharedWithMe(sharedData.reviews || []);
      } else {
        setSharedWithMe([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReview = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;

    try {
      setCreating(true);
      const response = await authFetch(`${config.API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim(), description: newDescription.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create review');
      }

      const data = await response.json();
      setReviews([data.review, ...reviews]);
      setNewTitle('');
      setNewDescription('');
      setShowCreateForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleShareReview = async (e, review) => {
    e.stopPropagation();
    try {
      let shareId = review.shareId;
      // If this review doesn't have a shareId yet, ask the server to generate one
      if (!shareId) {
        const response = await authFetch(`${config.API_URL}/api/reviews/${review.id}/share`, {
          method: 'POST',
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to enable sharing for this review');
        }
        const data = await response.json();
        shareId = data.review.shareId;
        // Update local state so future calls see the shareId
        setReviews((prev) =>
          prev.map((r) => (r.id === review.id ? { ...r, shareId } : r))
        );
      }
      if (!shareId) return;
      const url = `${window.location.origin}${window.location.pathname || ''}?shareReview=${shareId}`;
      navigator.clipboard.writeText(url).then(() => alert('Review link copied!'));
    } catch (err) {
      alert(err.message || 'Failed to copy review link');
    }
  };

  const handleShareLinkSubmit = async (e) => {
    e.preventDefault();
    if (!shareLinkInput.trim()) return;

    try {
      setLoadingShareLink(true);
      // Extract shareId from URL if full URL is provided
      let shareId = shareLinkInput.trim();
      const urlMatch = shareLinkInput.match(/shareReview=([^&]+)/);
      if (urlMatch) {
        shareId = urlMatch[1];
      }

      // Navigate to the shared review link
      window.location.href = `${window.location.pathname}?shareReview=${shareId}`;
    } catch (err) {
      alert(err.message || 'Failed to open shared review');
      setLoadingShareLink(false);
    }
  };

  if (loading) {
    return (
    <div className="reviews-container reviews-container--loading">
        <div className="reviews-loading-inline">
          <div className="loading-spinner" />
          <div className="loading-text">Loading your reviews…</div>
        </div>
    </div>
    );
  }

  const sharedWithMeOnly = sharedWithMe || [];

  let visibleReviews = reviews;
  if (activeGroup === 'sharedWithMe') {
    visibleReviews = sharedWithMeOnly;
  }

  // Count total items in grid (including create/share cards)
  let totalItems = visibleReviews.length;
  if (activeGroup === 'all' && !showCreateForm) {
    totalItems += 1; // create-review-card
  }
  if (activeGroup === 'sharedWithMe') {
    totalItems += 1; // share-review-link-card
  }

  // Determine grid class based on item count
  let gridClass = 'reviews-list';
  if (totalItems <= 2) {
    gridClass += ' reviews-list--full-width';
  } else {
    gridClass += ' reviews-list--three-columns';
  }

  return (
    <div className="reviews-container">
      <div className="reviews-header">
        <div>
          <h1>My Reviews (Projects)</h1>
          <p className="user-info">Welcome, {user?.name || user?.email}</p>
        </div>
        <button onClick={onLogout} className="logout-button">
          Logout
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Group toggle: All vs Shared */}
      <div className="reviews-group-toggle">
          <button
            type="button"
            className={`group-toggle-btn ${activeGroup === 'all' ? 'group-toggle-btn--active' : ''}`}
            onClick={() => setActiveGroup('all')}
          >
            My reviews
          </button>
          <button
            type="button"
            className={`group-toggle-btn ${activeGroup === 'sharedWithMe' ? 'group-toggle-btn--active' : ''}`}
            onClick={() => setActiveGroup('sharedWithMe')}
          >
            Shared with me
          </button>
        </div>

      {showCreateForm && (
        <form onSubmit={handleCreateReview} className="create-review-form">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Review title (required)"
            required
            disabled={creating}
            className="review-title-input"
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (optional)"
            disabled={creating}
            className="review-description-input"
            rows="3"
          />
          <div className="create-review-actions">
            <button type="submit" disabled={creating || !newTitle.trim()} className="submit-button">
              {creating ? 'Creating...' : 'Create Review'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setNewTitle('');
                setNewDescription('');
              }}
              disabled={creating}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className={gridClass}>
        {activeGroup === 'all' && !showCreateForm && (
          <button
            type="button"
            className="create-review-card"
            onClick={() => setShowCreateForm(true)}
          >
            <div className="create-review-card-icon">＋</div>
            <div className="create-review-card-text">
              <h3>Create new review</h3>
              <p>Start a new project to organize your PDFs.</p>
            </div>
          </button>
        )}
        {activeGroup === 'sharedWithMe' && (
          <div className="share-review-link-card">
            <div className="share-review-link-card-icon">🔗</div>
            <div className="share-review-link-card-content">
              <h3>Share a review</h3>
              <p>Paste a review share link to access it</p>
              <form onSubmit={handleShareLinkSubmit} className="share-link-form">
                <input
                  type="text"
                  value={shareLinkInput}
                  onChange={(e) => setShareLinkInput(e.target.value)}
                  placeholder="Paste review share link here"
                  disabled={loadingShareLink}
                  className="share-link-input"
                />
                <button
                  type="submit"
                  disabled={loadingShareLink || !shareLinkInput.trim()}
                  className="share-link-submit-btn"
                >
                  {loadingShareLink ? 'Opening...' : 'Open'}
                </button>
              </form>
            </div>
          </div>
        )}
        {visibleReviews.length > 0 && visibleReviews.map((review) => (
            <div
              key={review.id}
              className="review-card"
              onClick={() => onSelectReview(review)}
            >
              <button
                type="button"
                className="review-delete-btn"
                onClick={(e) => handleDeleteReview(e, review.id)}
                aria-label="Delete review"
                title="Delete review"
              >
                ✕
              </button>
              {review.shareId && (
                <button
                  type="button"
                  className="share-review-btn"
                  onClick={(e) => handleShareReview(e, review)}
                >
                  Share
                </button>
              )}
              <h3>{review.title}</h3>
              {review.description && <p className="review-description">{review.description}</p>}
              <p className="review-meta">
                Created {new Date(review.createdAt).toLocaleDateString()}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ReviewsList;
